/* ==========================================================================
   ALBEUX REVEAL SCRIPT (assets/albeux-reveal.js, pairs with assets/albeux-reveal.css)

   PART 1: THE HEADING REVEAL
   When a collection page opens (Albeux Collections, Handbags, Women, Men ... every collection) or the Collections list opens, its heading is cut into letters on screen.
   The FIRST letter stays exactly where it belongs. The other letters come out from behind it and slide to the right into their places, fading in as they leave.
   It is not the brand name reveal: nothing moves the first letter, nothing blurs, nothing zooms.

   It is locked to the white sweep that slides down the product photographs (albeux-grid-fx.js): SAME START, SAME END, SAME EASING, SAME LENGTH.
   (31 Oct 2026, sync fix) What used to make the heading late on some collections, and what is gone now:
     1. Letters had a stagger (each one waited for the one before it). The longer the title, the later the last letters began. There is NO stagger now:
        every letter starts on the same frame as the sweep and lands on the same frame as the sweep. The letters still fan out one after another
        because the ones further along the word have further to travel.
     2. The heading waited for the web fonts before it could start. It no longer waits: the moment the sweep starts, the heading starts.
     3. This script is loaded after the grid script, so on a slow first visit the sweep could already have started before this script was ready to hear it
        (the heading then waited about 2.4 seconds and played alone). The grid script now leaves a timestamp (window.__albSweepAt) and the heading reads it:
        if the sweep is already running it joins it at the very same point of its movement (a negative delay), so it still lands at the very same moment.
   The sweep time is read from --alb-wipe-time (1.8 seconds), so if that number is ever changed the heading follows it.
   Where there is no sweep (the Collections list, an empty collection, or a first row of photographs that is below the fold) the heading plays by itself
   after the page is ready, with the same 1.8 seconds and the same easing.

   Safety: if anything is missing the heading is simply shown as it is. A heading can never stay hidden: the page hides it for 4 seconds at most (layout/theme.liquid),
   the letters are shown after 6 seconds whatever happens, and visitors who ask their device for reduced motion get no effect at all.
   Only transform and opacity move, so phones stay smooth. The theme editor is left alone.

   PART 2: THE MENU (second block of this file)
   When the menu is opened (the slide-in menu on a phone, and every sub-menu inside it), each text comes in from the left, one after another, much faster than the stock menu:
   about 40 milliseconds between lines (the stock one waited 100 milliseconds plus a start delay). Each line slides in from the left while a mask opens from left to right
   and a soft blur clears, with a long, silky landing. The little arrows and plus signs follow their line, the hairlines between the groups draw themselves from the left
   as their group arrives, and the language / account block arrives last.
   On a computer the drop-down links under Collections, Women and Men come in the same way when the mouse reaches them.
   Only transform, opacity, a mask and a small blur move. Nothing is left behind afterwards: when a line has arrived, the browser goes back to the menu's normal styles.
   ========================================================================== */
(function () {
  if (window.__albeuxHeadingReveal) return;
  window.__albeuxHeadingReveal = true;
  var doc = document.documentElement;
  function unpend() { doc.classList.remove('alb-rv-pending'); }
  try {
    if (doc.classList.contains('shopify-design-mode') || !Element.prototype.animate || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) { unpend(); return; }
  } catch (e) { unpend(); return; }

  /* The very same curve as the white sweep (alb-wipe in albeux-grid-fx.css) */
  var EASE = 'cubic-bezier(0.5, 0, 0.15, 1)';
  var main = document.getElementById('MainContent');
  var tpl = main ? (main.getAttribute('data-template') || '') : '';
  var onCollection = tpl.indexOf('collection') === 0;
  var onList = tpl.indexOf('list-collections') === 0;
  if (!onCollection && !onList) { unpend(); return; }
  var h = onCollection ? main.querySelector('h1.albeux-collection-title') : main.querySelector('h1');
  if (!h || h.children.length) { unpend(); return; }
  var text = (h.textContent || '').replace(/\s+/g, ' ').trim();
  if (text.length < 2) { unpend(); return; }

  /* Cut the heading into letters (words stay whole, spaces stay normal spaces) */
  h.setAttribute('aria-label', text);
  h.setAttribute('data-no-reveal', '');
  h.textContent = '';
  var chars = [];
  text.split(' ').forEach(function (word) {
    if (!word) return;
    if (chars.length) h.appendChild(document.createTextNode(' '));
    var w = document.createElement('span');
    w.className = 'alb-rv__w';
    w.setAttribute('aria-hidden', 'true');
    for (var k = 0; k < word.length; k++) {
      var c = document.createElement('span');
      c.className = 'alb-rv__c';
      c.textContent = word.charAt(k);
      w.appendChild(c);
      chars.push(c);
    }
    h.appendChild(w);
  });
  chars[0].className += ' alb-rv__c--a';
  h.classList.add('alb-rv', 'alb-rv--wait');
  unpend();

  var sweepAt = 0;
  var played = false;
  var failTimer = 0;

  /* The sweep time, read from the collection page (1.8 seconds) */
  function sweepTime() {
    try {
      var v = window.getComputedStyle(main).getPropertyValue('--alb-wipe-time').trim();
      var n = parseFloat(v);
      if (!n) return 1800;
      return /ms$/.test(v) ? n : n * 1000;
    } catch (e) { return 1800; }
  }

  function finish() {
    h.classList.remove('alb-rv--wait', 'alb-rv--anim');
    h.classList.add('alb-rv--done');
  }

  /* Every letter: same start, same length, same curve as the sweep. If the sweep is already running, join it at its own point (negative delay). */
  function play() {
    if (played) return;
    played = true;
    clearTimeout(failTimer);
    try {
      var now = performance.now();
      var total = sweepTime();
      var elapsed = sweepAt ? Math.max(0, now - sweepAt) : 0;
      var n = chars.length - 1;
      if (n < 1 || total - elapsed < 120) { finish(); return; }
      /* Read every position first ... */
      var rects = chars.map(function (c) { return c.getBoundingClientRect(); });
      var ar = rects[0];
      /* ... then start every letter on the same frame */
      var running = n;
      h.classList.add('alb-rv--anim');
      for (var i = 1; i <= n; i++) {
        (function (c, r) {
          var dx = ar.left - r.left;
          var dy = ar.top - r.top;
          var a = c.animate([
            { transform: 'translate3d(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px,0)', opacity: 0, offset: 0 },
            { opacity: 1, offset: 0.4 },
            { transform: 'translate3d(0,0,0)', opacity: 1, offset: 1 }
          ], { duration: total, delay: -elapsed, easing: EASE, fill: 'both' });
          a.onfinish = function () {
            try { a.cancel(); } catch (e) {}
            running--;
            if (running <= 0) finish();
          };
        })(chars[i], rects[i]);
      }
      /* The letters now belong to their animations: the waiting state can go in the same moment */
      h.classList.remove('alb-rv--wait');
    } catch (e) { finish(); }
  }

  /* The sweep on the product photographs has started: start on the very same frame, no waiting for fonts */
  function onSweep(at) {
    if (played) return;
    if (!sweepAt) sweepAt = at;
    play();
  }
  window.addEventListener('alb:sweep', function () { onSweep(performance.now()); });
  /* The sweep started before this script was ready: join it where it is */
  if (window.__albSweepAt) onSweep(window.__albSweepAt);

  /* No sweep to follow (the Collections list, an empty collection, a first row below the fold): play alone once the fonts are ready (1.2 seconds at most) */
  function arm() {
    if (played) return;
    var wait = 500;
    if (onCollection) {
      var first = main.querySelector('.product-grid__item .card-gallery');
      if (first) {
        var r = first.getBoundingClientRect();
        /* First row of photographs on screen: the sweep is about to start, wait for it. Below the fold: do not wait for a scroll. */
        if (r.height > 0 && r.top < (window.innerHeight || 800) * 0.7) wait = 1600;
      }
    }
    failTimer = setTimeout(play, wait);
  }
  var fonts = (document.fonts && document.fonts.ready)
    ? Promise.race([document.fonts.ready, new Promise(function (res) { setTimeout(res, 1200); })])
    : Promise.resolve();
  fonts.then(arm, arm);

  /* Coming back with the back button: the heading is simply shown */
  window.addEventListener('pageshow', function (ev) { if (ev.persisted) { played = true; finish(); } });
  setTimeout(function () { if (!played) { played = true; } finish(); }, 6000);
})();

/* ==========================================================================
   PART 2: THE MENU
   ========================================================================== */
(function () {
  if (window.__albeuxMenuReveal) return;
  window.__albeuxMenuReveal = true;
  var doc = document.documentElement;
  try {
    if (doc.classList.contains('shopify-design-mode') || !Element.prototype.animate || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
  } catch (e) { return; }
  /* From now on the stock menu animation is switched off in the stylesheet and this script does the movement */
  doc.classList.add('alb-mi');

  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var HOME = 'translate3d(0,0,0)';
  var live = [];
  var liveDesk = [];

  function shown(el) { return el.getClientRects().length > 0; }

  function stop(list) {
    for (var i = 0; i < list.length; i++) { try { list[i].cancel(); } catch (e) {} }
    list.length = 0;
  }

  /* One piece of the menu comes in from the left. kind 'text': slide + mask opening left to right + blur clearing. kind 'icon': slide + fade. */
  function go(list, el, delay, kind) {
    var text = kind === 'text';
    var from = text
      ? { opacity: 0, transform: 'translate3d(-34px,0,0)', clipPath: 'inset(0 100% 0 0)', filter: 'blur(6px)' }
      : { opacity: 0, transform: 'translate3d(-20px,0,0)' };
    var to = text
      ? { opacity: 1, transform: HOME, clipPath: 'inset(0 0% 0 0)', filter: 'blur(0px)' }
      : { opacity: 1, transform: HOME };
    try {
      list.push(el.animate([from, to], { duration: text ? 720 : 560, delay: delay, easing: EASE, fill: 'backwards' }));
    } catch (e) {}
  }

  /* A hairline between two groups draws itself from the left (the real border is hidden while it draws, then it is the real border again) */
  function line(list, li, delay) {
    try {
      var cs = window.getComputedStyle(li);
      var w = parseFloat(cs.borderBottomWidth);
      if (!w || cs.borderBottomStyle === 'none') return;
      var col = cs.borderBottomColor;
      var base = {
        borderBottomColor: 'rgba(0,0,0,0)',
        backgroundImage: 'linear-gradient(' + col + ',' + col + ')',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left bottom',
        backgroundOrigin: 'border-box'
      };
      var a = {}; var b = {}; var k;
      for (k in base) { a[k] = base[k]; b[k] = base[k]; }
      a.backgroundSize = '0% ' + w + 'px';
      b.backgroundSize = '100% ' + w + 'px';
      var an = li.animate([a, b], { duration: 760, delay: delay, easing: EASE, fill: 'both' });
      an.onfinish = function () { try { an.cancel(); } catch (e) {} };
      list.push(an);
    } catch (e) {}
  }

  /* Opens one panel of the slide-in menu: the first level, or a sub-menu panel */
  function run(root, lead) {
    if (!root) return;
    stop(live);
    var texts = [].slice.call(root.querySelectorAll('.menu-drawer__menu-item-text')).filter(shown);
    var n = texts.length;
    var step = n > 1 ? Math.min(42, 620 / (n - 1)) : 0;
    texts.forEach(function (t, i) {
      var d = lead + i * step;
      go(live, t, d, 'text');
      var row = t.closest('.menu-drawer__menu-item');
      if (row) {
        for (var k = 0; k < row.children.length; k++) {
          if (row.children[k].classList.contains('svg-wrapper')) go(live, row.children[k], d + 60, 'icon');
        }
      }
    });
    var lis = root.querySelectorAll('li.menu-drawer__list-item');
    for (var j = 0; j < lis.length; j++) {
      if (!shown(lis[j])) continue;
      var first = lis[j].querySelector('.menu-drawer__menu-item-text');
      var idx = first ? texts.indexOf(first) : -1;
      if (idx >= 0) line(live, lis[j], lead + idx * step);
    }
    var util = root.querySelector('.menu-drawer__utility-links');
    if (util && shown(util) && util.children.length) go(live, util, lead + n * step + 40, 'icon');
  }

  /* Watch each menu drawer: the stock script adds the class menu-open to the details element that opens (the whole menu, or a sub-menu) */
  function watch(drawer) {
    if (drawer.__albMi) return;
    drawer.__albMi = true;
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var t = muts[i].target;
          if (!t || t.tagName !== 'DETAILS') continue;
          var open = t.classList.contains('menu-open');
          if (open === !!t.__albOpen) continue;
          t.__albOpen = open;
          if (!open) continue;
          if (t.classList.contains('menu-drawer-container')) run(t.querySelector('.menu-drawer'), 150);
          else run(t.querySelector('.menu-drawer__submenu'), 60);
        }
      }).observe(drawer, { attributes: true, subtree: true, attributeFilter: ['class'] });
    } catch (e) {}
  }

  function attach() {
    var list = document.querySelectorAll('header-drawer');
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }
  attach();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach);
  window.addEventListener('load', attach);
  document.addEventListener('shopify:section:load', attach);
  document.addEventListener('pointerdown', attach, { passive: true, once: true });

  /* The drop-down on a computer: when the mouse reaches Collections, Women or Men, its links come in one by one */
  var fine = false;
  try { fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) {}
  if (fine) {
    var lastLi = null;
    document.addEventListener('pointerover', function (ev) {
      var li = ev.target && ev.target.closest ? ev.target.closest('#header-component .menu-list__list-item') : null;
      if (!li) { lastLi = null; return; }
      if (li === lastLi) return;
      lastLi = li;
      var links = li.querySelectorAll('.menu-list__submenu .mega-menu__link');
      if (!links.length) return;
      stop(liveDesk);
      var n = links.length;
      var step = n > 1 ? Math.min(34, 420 / (n - 1)) : 0;
      for (var i = 0; i < n; i++) go(liveDesk, links[i], 80 + i * step, 'text');
    }, { passive: true });
  }
})();
